import supabase, { isSupabaseConfigured } from './supabaseClient';
import { getRouteBySlug } from '../data/routeData';

const VISITOR_COOKIE = 'trishul_visitor_id';
const LOCAL_STORAGE_VISITOR_KEY = 'trishul_visitor_id';
const SESSION_ID_KEY = 'trishul_session_id';
const SESSION_CLICKS_KEY = 'trishul_session_clicks';
const GEO_CACHE_KEY = 'trishul_visitor_geo';
const LOCAL_EVENTS_KEY = 'trishul_analytics_events';
const LOCAL_VISITS_KEY = 'trishul_visitor_log';
const LOCAL_CONTACTS_KEY = 'trishul_contact_submissions';
const LOCAL_PAGE_CLICKS_KEY = 'trishul_page_clicks';

function getBrowserName() {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Browser';
}

export const masterPageSeo = {
  '/': {
    title: 'Trishul Tours & Travels | Taxi & Outstation Cabs',
    description: 'Book one-way and round-trip cabs with Trishul Tours & Travels. Explore intercity taxi options, airport transfers and outstation trips. Request your fare on WhatsApp.',
    keywords: 'taxi service vapi, cab booking daman, car rental silvassa, outstation cabs vapi, airport taxi mumbai, trishul travels',
  },
  '/routes': {
    title: 'Taxi Routes & Outstation Cab Booking Directory | Trishul Travels',
    description: 'Browse 48 verified taxi routes connecting Vapi, Bhilad, Daman, Silvassa, Valsad & Umbergaon with Mumbai Airport, Surat, Ahmedabad, Nashik & Pune.',
    keywords: 'taxi routes directory, outstation cabs gujarat maharashtra, airport taxi routes, vapi to mumbai cab routes',
  },
  '/services': {
    title: 'Services | Taxi Rental, Outstation Cabs & Airport Transfers - Trishul Travels',
    description: 'Explore outstation round-trip cabs, one-way taxi rentals, 24/7 airport transfers, local hourly packages, and group mini buses with drivers.',
    keywords: 'car rental services, outstation taxi, airport transfer, corporate cab, 24/7 taxi rental',
  },
  '/fleet': {
    title: 'Our Fleet | Swift Dzire, Innova Crysta, Ertiga, XUV700 & Traveller - Trishul Travels',
    description: 'View our clean, well-maintained fleet of Sedans, MUVs, Luxury SUVs, and 17-Seater Force Travellers available for rental in Vapi, Daman, Silvassa.',
    keywords: 'swift dzire cab, innova crysta rental, ertiga taxi, force traveller 17 seater, wedding car rental',
  },
  '/booking': {
    title: 'Book Your Vehicle | Instant Online Cab & Rental Request - Trishul Travels',
    description: 'Reserve your vehicle online in under 2 minutes. Instant booking requests for one-way, round-trip outstation cabs and airport transfers.',
    keywords: 'cab booking, taxi reservation, online car rental, get best fare taxi, outstation cab booking',
  },
  '/contact': {
    title: 'Contact Us | Trishul Tours & Travels - 24/7 Helpline +91 83203 42657',
    description: 'Get in touch with Trishul Tours & Travels in Sarigam / Vapi. Call or WhatsApp +91 83203 42657 for quick quotes and 24/7 support.',
    keywords: 'contact trishul travels, customer care taxi, phone number taxi vapi, cab booking helpline',
  },
  '/faqs': {
    title: 'FAQs | Common Questions About Car Rental & Cab Booking - Trishul Travels',
    description: 'Find answers to common questions about booking, documents required, advance payments, and outstation taxi policies.',
    keywords: 'taxi faqs, car rental cancellation, cab advance payment, outstation taxi policies',
  },
  '/about': {
    title: 'About Us | Trishul Tours & Travels - Vapi & Sarigam Cab Service',
    description: 'Learn about Trishul Tours & Travels based in Vapi & Sarigam. Safety, punctuality, clean vehicles, and 5.0★ Google rated service.',
    keywords: 'about trishul travels, cab service vapi, verified drivers sarigam, taxi company gujarat',
  },
};

export function getPageSeoMetadata(pathname) {
  if (!pathname) return masterPageSeo['/'];
  const cleanPath = pathname.split('?')[0].replace(/\/$/, '') || '/';

  if (masterPageSeo[cleanPath]) {
    return masterPageSeo[cleanPath];
  }

  if (cleanPath.startsWith('/routes/')) {
    const slug = cleanPath.replace('/routes/', '');
    try {
      const route = getRouteBySlug(slug);
      if (route) {
        return {
          title: route.seoTitle || `${route.originName} to ${route.destinationName} Taxi & Cab | Trishul Tours`,
          description: route.seoDescription || `Book taxi from ${route.originName} to ${route.destinationName} with Trishul Tours & Travels.`,
          keywords: `${route.originName} to ${route.destinationName} taxi, ${route.originName} to ${route.destinationName} cab, ${route.originName} car rental, ${route.destinationName} outstation cab`,
        };
      }
    } catch {}
  }

  return {
    title: `${cleanPath.replace('/', '').toUpperCase()} | Trishul Tours & Travels`,
    description: 'Trishul Tours & Travels provides 24/7 taxi and car rental service.',
    keywords: 'trishul travels, car rental, taxi service',
  };
}

export function getVisitorId() {
  if (typeof document === 'undefined') return 'server-visitor';
  try {
    const existingCookie = document.cookie.split('; ').find((row) => row.startsWith(`${VISITOR_COOKIE}=`))?.split('=')[1];
    if (existingCookie) return existingCookie;

    const existingLocal = localStorage.getItem(LOCAL_STORAGE_VISITOR_KEY);
    if (existingLocal) {
      document.cookie = `${VISITOR_COOKIE}=${existingLocal}; Max-Age=31536000; Path=/; SameSite=Lax`;
      return existingLocal;
    }

    const newId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `vis-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    document.cookie = `${VISITOR_COOKIE}=${newId}; Max-Age=31536000; Path=/; SameSite=Lax`;
    localStorage.setItem(LOCAL_STORAGE_VISITOR_KEY, newId);
    return newId;
  } catch {
    return 'fallback-visitor';
  }
}

export function getSessionId() {
  if (typeof sessionStorage === 'undefined') return 'server-session';
  try {
    let sid = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sid) {
      sid = `ses-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      sessionStorage.setItem(SESSION_ID_KEY, sid);
    }
    return sid;
  } catch {
    return 'fallback-session';
  }
}

export function getSessionClicks() {
  if (typeof sessionStorage === 'undefined') return 0;
  try {
    return Number(sessionStorage.getItem(SESSION_CLICKS_KEY) || 0);
  } catch {
    return 0;
  }
}

export function incrementSessionClicks() {
  if (typeof sessionStorage === 'undefined') return 1;
  try {
    const current = getSessionClicks() + 1;
    sessionStorage.setItem(SESSION_CLICKS_KEY, String(current));
    return current;
  } catch {
    return 1;
  }
}

export function recordPageClick(pagePath) {
  if (typeof window === 'undefined' || !pagePath || pagePath.startsWith('/admin')) return;
  try {
    const raw = localStorage.getItem(LOCAL_PAGE_CLICKS_KEY) || '{}';
    const map = JSON.parse(raw);
    map[pagePath] = (map[pagePath] || 0) + 1;
    localStorage.setItem(LOCAL_PAGE_CLICKS_KEY, JSON.stringify(map));
  } catch {}
}

export function getPageClicksMap() {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(LOCAL_PAGE_CLICKS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function clearLocalAnalyticsData() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(LOCAL_VISITS_KEY);
    localStorage.removeItem(LOCAL_EVENTS_KEY);
    localStorage.removeItem(LOCAL_PAGE_CLICKS_KEY);
    localStorage.removeItem(LOCAL_CONTACTS_KEY);
    localStorage.removeItem(SESSION_CLICKS_KEY);
    localStorage.removeItem(GEO_CACHE_KEY);
  } catch {}
}

export async function getVisitorLocation() {
  if (typeof window === 'undefined') {
    return { ip: '127.0.0.1', city: 'Local Dev', region: '', country: 'Local', device: 'Desktop', browser: 'Node' };
  }

  try {
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {}

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1';

  const detected = {
    ip: isLocalhost ? '127.0.0.1 (Local Dev)' : 'Pending IP Resolution',
    city: isLocalhost ? 'Local Development' : 'Resolving Location...',
    region: '',
    country: isLocalhost ? 'Local' : '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
    device: /Mobile|Android|iP(hone|od|ad)/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
    browser: getBrowserName(),
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);

    const res = await fetch('https://ipwho.is/', { signal: controller.signal })
      .then((r) => r.json())
      .catch(() => null);

    clearTimeout(timeoutId);

    if (res && (res.success || res.ip)) {
      detected.ip = res.ip;
      detected.city = res.city || 'Unknown City';
      detected.region = res.region || '';
      detected.country = res.country || 'India';
      detected.postal = res.postal || '';
    } else {
      const ipRes = await fetch('https://api.ipify.org?format=json')
        .then((r) => r.json())
        .catch(() => null);
      if (ipRes?.ip) {
        detected.ip = ipRes.ip;
        detected.city = 'Detected Online';
        detected.country = 'India';
      }
    }
  } catch {}

  try {
    sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(detected));
  } catch {}

  return detected;
}

function forwardToGA4(eventName, params = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventName, params);
    } catch {}
  }
}

export async function trackAnalyticsEvent(eventType, metadata = {}) {
  try {
    forwardToGA4(eventType, metadata);

    const geo = metadata.ip ? { ip: metadata.ip, city: metadata.city, region: metadata.region, country: metadata.country } : await getVisitorLocation();
    const visitorId = getVisitorId();
    const pagePath = typeof window !== 'undefined' ? window.location.pathname : '';
    const nowIso = new Date().toISOString();
    const seo = getPageSeoMetadata(pagePath);

    const localEvent = {
      id: `ev-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      event_type: eventType,
      visitor_id: visitorId,
      session_id: getSessionId(),
      ip: geo.ip,
      city: geo.city,
      region: geo.region,
      country: geo.country,
      page_path: pagePath,
      page_title: seo.title,
      meta_description: seo.description,
      keywords: seo.keywords,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
      referrer: typeof document !== 'undefined' ? document.referrer.slice(0, 300) : '',
      metadata: { ...metadata, ...geo, page_title: seo.title, meta_description: seo.description, keywords: seo.keywords },
      created_at: nowIso,
    };

    // Save to local events list (capped at 500)
    try {
      const raw = localStorage.getItem(LOCAL_EVENTS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      localStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify([localEvent, ...list.slice(0, 499)]));
    } catch {}

    if (!isSupabaseConfigured) return;

    const logToDb = async () => {
      try {
        // Enforce valid Supabase check constraint ('visit', 'whatsapp_click', 'call_click')
        let safeEventType = eventType;
        if (!['visit', 'whatsapp_click', 'call_click'].includes(eventType)) {
          safeEventType = 'visit';
        }

        const row = {
          event_type: safeEventType,
          visitor_id: visitorId,
          ip_hash: geo.ip || null,
          page_path: pagePath,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : '',
          referrer: typeof document !== 'undefined' ? document.referrer.slice(0, 500) : '',
          metadata: { ...metadata, ...geo, page_title: seo.title, meta_description: seo.description, keywords: seo.keywords, original_event_type: eventType },
        };
        await supabase.from('analytics_events').insert(row);
      } catch {}
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(logToDb, { timeout: 3500 });
    } else {
      setTimeout(logToDb, 1500);
    }
  } catch {}
}

export function trackClick(eventType, metadata = {}) {
  Promise.resolve(trackAnalyticsEvent(eventType, metadata)).catch(() => {});
}

export function trackRouteView(slug, origin, destination) {
  trackClick('route_view', { route_slug: slug, origin, destination });
}

export function trackWhatsAppClick(source, metadata = {}) {
  trackClick('whatsapp_click', { source, channel: 'WhatsApp', ...metadata });
}

export function trackCallClick(source, metadata = {}) {
  trackClick('call_click', { source, channel: 'Call', ...metadata });
}

export function trackBookingSubmission(metadata = {}) {
  trackClick('booking_submission', metadata);
}

export async function trackPageView(pagePath = typeof window !== 'undefined' ? window.location.pathname : '') {
  if (typeof window === 'undefined') return;
  if (pagePath.startsWith('/admin')) return;

  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  const geo = await getVisitorLocation();
  const seo = getPageSeoMetadata(pagePath);

  const visitRecord = {
    id: `visit-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    visitor_id: visitorId,
    session_id: sessionId,
    ip: geo.ip,
    city: geo.city,
    region: geo.region,
    country: geo.country,
    device: geo.device,
    browser: geo.browser,
    page_path: pagePath,
    page_title: seo.title,
    meta_description: seo.description,
    keywords: seo.keywords,
    referrer: document.referrer || 'Direct / Organic',
    user_agent: navigator.userAgent.slice(0, 300),
    timestamp: new Date().toISOString(),
    clicks: getSessionClicks(),
  };

  try {
    const raw = localStorage.getItem(LOCAL_VISITS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    localStorage.setItem(LOCAL_VISITS_KEY, JSON.stringify([visitRecord, ...list.slice(0, 499)]));
  } catch {}

  trackAnalyticsEvent('visit', {
    action: 'page_view',
    ...geo,
    page: pagePath,
    page_title: seo.title,
    meta_description: seo.description,
    keywords: seo.keywords,
    session_id: sessionId,
  });
}

export async function trackContactFormSubmission(formData) {
  const geo = await getVisitorLocation();
  const submission = {
    id: `contact-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    name: formData.name || '',
    mobile: formData.mobile || '',
    email: formData.email || '',
    pickup_location: formData.pickup_location || '',
    destination: formData.destination || '',
    message: formData.message || '',
    travel_date: formData.travel_date || '',
    passengers: formData.passengers || 2,
    ip: geo.ip,
    city: geo.city,
    region: geo.region,
    country: geo.country,
    created_at: new Date().toISOString(),
    status: 'new',
  };

  try {
    const raw = localStorage.getItem(LOCAL_CONTACTS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    localStorage.setItem(LOCAL_CONTACTS_KEY, JSON.stringify([submission, ...list]));
  } catch {}

  trackAnalyticsEvent('visit', {
    action: 'contact_form_submitted',
    name: submission.name,
    mobile: submission.mobile,
    email: submission.email,
    ip: geo.ip,
    city: geo.city,
  });
}

export async function trackBookRideSubmission(booking) {
  const geo = await getVisitorLocation();
  trackAnalyticsEvent('visit', {
    action: 'book_ride_submitted',
    reference: booking.reference || '',
    customer_name: booking.customer_name || '',
    mobile_number: booking.mobile_number || '',
    route: `${booking.pickup_location || ''} -> ${booking.drop_location || ''}`,
    vehicle: booking.vehicle_name || '',
    ip: geo.ip,
    city: geo.city,
  });
}

let isClickListenerInitialized = false;

export function initAnalyticsTracking() {
  if (typeof window === 'undefined' || isClickListenerInitialized) return;
  isClickListenerInitialized = true;

  window.addEventListener(
    'click',
    (event) => {
      const totalClicks = incrementSessionClicks();
      const currentPath = window.location.pathname;

      if (!currentPath.startsWith('/admin')) {
        recordPageClick(currentPath);
      }

      const target = event.target?.closest?.('a, button');
      if (!target) return;

      const href = (target.getAttribute('href') || '').toLowerCase();
      const text = (target.textContent || '').toLowerCase();
      const id = (target.id || '').toLowerCase();
      const className = (target.className || '').toLowerCase();

      if (href.includes('wa.me') || href.includes('whatsapp') || id.includes('whatsapp') || className.includes('whatsapp')) {
        trackWhatsAppClick(window.location.pathname, {
          target_href: href,
          button_text: (target.textContent || '').trim().slice(0, 60),
          total_session_clicks: totalClicks,
        });
        return;
      }

      if (href.startsWith('tel:') || id.includes('call') || className.includes('call-now')) {
        trackCallClick(window.location.pathname, {
          phone_number: href.replace('tel:', ''),
          button_text: (target.textContent || '').trim().slice(0, 60),
          total_session_clicks: totalClicks,
        });
        return;
      }

      if (text.includes('enquire') || text.includes('quote') || id.includes('enquire') || className.includes('enquire')) {
        trackClick('enquiry_click', {
          source_page: window.location.pathname,
          button_text: (target.textContent || '').trim().slice(0, 60),
          total_session_clicks: totalClicks,
        });
        return;
      }
    },
    { passive: true }
  );
}

// ==========================================
// ADMIN REPORTING & AGGREGATION HELPERS
// ==========================================

export async function fetchAdminAnalyticsData() {
  let dbEvents = [];
  let dbBookings = [];
  let dbEnquiries = [];

  if (isSupabaseConfigured) {
    try {
      const [evRes, bkRes, enRes] = await Promise.all([
        supabase.from('analytics_events').select('*').order('created_at', { ascending: false }).limit(2000),
        supabase.from('bookings').select('*').order('created_at', { ascending: false }),
        supabase.from('enquiries').select('*').order('created_at', { ascending: false }),
      ]);
      if (evRes.data) dbEvents = evRes.data;
      if (bkRes.data) dbBookings = bkRes.data;
      if (enRes.data) dbEnquiries = enRes.data;
    } catch {}
  }

  // Live Launch Date: Only entries from today (2026-10-20 onwards) are included
  const LIVE_LAUNCH_DATE = '2026-10-20';
  const isFromLaunchDate = (dateStr) => {
    if (!dateStr) return false;
    return dateStr.slice(0, 10) >= LIVE_LAUNCH_DATE;
  };

  dbEvents = dbEvents.filter((ev) => isFromLaunchDate(ev.created_at));
  dbBookings = dbBookings.filter((bk) => isFromLaunchDate(bk.created_at));
  dbEnquiries = dbEnquiries.filter((en) => isFromLaunchDate(en.created_at));

  let localVisits = [];
  let localEvents = [];
  let localContacts = [];
  let localBookings = [];

  try {
    const rawVisits = localStorage.getItem(LOCAL_VISITS_KEY);
    if (rawVisits) {
      localVisits = JSON.parse(rawVisits).filter((v) => isFromLaunchDate(v.timestamp));
      localStorage.setItem(LOCAL_VISITS_KEY, JSON.stringify(localVisits));
    }
    const rawEvents = localStorage.getItem(LOCAL_EVENTS_KEY);
    if (rawEvents) {
      localEvents = JSON.parse(rawEvents).filter((e) => isFromLaunchDate(e.created_at));
      localStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify(localEvents));
    }
    const rawContacts = localStorage.getItem(LOCAL_CONTACTS_KEY);
    if (rawContacts) {
      localContacts = JSON.parse(rawContacts).filter((c) => isFromLaunchDate(c.created_at));
      localStorage.setItem(LOCAL_CONTACTS_KEY, JSON.stringify(localContacts));
    }
    const rawBookings = localStorage.getItem('trishul_local_bookings');
    if (rawBookings) {
      localBookings = JSON.parse(rawBookings).filter((b) => isFromLaunchDate(b.created_at));
      localStorage.setItem('trishul_local_bookings', JSON.stringify(localBookings));
    }
  } catch {}

  const pageClicks = getPageClicksMap();

  // Normalized Visits (IP & Location)
  const visitMap = new Map();

  // Load from local visits
  localVisits.forEach((v) => {
    const key = `${v.ip || 'ip'}_${v.timestamp?.slice(0, 16)}`;
    const seo = getPageSeoMetadata(v.page_path || '/');
    visitMap.set(key, {
      id: v.id,
      timestamp: v.timestamp || new Date().toISOString(),
      ip: v.ip || 'Unknown IP',
      city: v.city || 'Resolving Location',
      region: v.region || '',
      country: v.country || '',
      page_path: v.page_path || '/',
      page_title: v.page_title || seo.title,
      meta_description: v.meta_description || seo.description,
      keywords: v.keywords || seo.keywords,
      device: v.device || 'Desktop',
      browser: v.browser || 'Browser',
      clicks: Number(v.clicks) || 0,
    });
  });

  // Merge cloud analytics events
  dbEvents.forEach((ev) => {
    const meta = ev.metadata || {};
    const ip = ev.ip_hash || meta.ip || 'Unknown IP';
    const key = `${ip}_${ev.created_at?.slice(0, 16)}`;
    const path = ev.page_path || meta.page || '/';
    const seo = getPageSeoMetadata(path);

    if (!visitMap.has(key)) {
      visitMap.set(key, {
        id: ev.id,
        timestamp: ev.created_at,
        ip,
        city: meta.city || 'Online',
        region: meta.region || '',
        country: meta.country || '',
        page_path: path,
        page_title: meta.page_title || seo.title,
        meta_description: meta.meta_description || seo.description,
        keywords: meta.keywords || seo.keywords,
        device: meta.device || 'Mobile',
        browser: meta.browser || 'Browser',
        clicks: Number(meta.total_session_clicks) || 0,
      });
    }
  });

  const allVisits = Array.from(visitMap.values()).sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  // Normalized Bookings
  const bookingIds = new Set();
  const allBookings = [];
  [...localBookings, ...dbBookings].forEach((b) => {
    const idKey = b.id || b.reference;
    if (idKey && !bookingIds.has(idKey)) {
      bookingIds.add(idKey);
      allBookings.push({
        id: b.id || idKey,
        reference: b.reference || '',
        created_at: b.created_at || new Date().toISOString(),
        customer_name: b.customer_name || b.customer?.full_name || 'Customer',
        mobile_number: b.mobile_number || b.customer?.mobile || '',
        trip_type: b.trip_type || 'One Way',
        vehicle_name: b.vehicle_name || b.vehicle?.name || '-',
        pickup_location: b.pickup_location || '-',
        drop_location: b.drop_location || '-',
        pickup_date: b.pickup_date || '',
        status: b.status || 'pending',
      });
    }
  });
  allBookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Normalized Contact Submissions
  const contactIds = new Set();
  const allContacts = [];
  [...localContacts, ...dbEnquiries].forEach((c) => {
    const idKey = c.id || `${c.mobile}_${c.created_at}`;
    if (!contactIds.has(idKey)) {
      contactIds.add(idKey);
      allContacts.push({
        id: c.id || idKey,
        created_at: c.created_at || new Date().toISOString(),
        name: c.name || 'Website Contact',
        mobile: c.mobile || '',
        email: c.email || '',
        pickup_location: c.pickup_location || '',
        destination: c.destination || '',
        message: c.message || '',
        status: c.status || 'new',
        city: c.city || '-',
      });
    }
  });
  allContacts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Normalized Enquiries / Clicks
  const allEnquiries = [];
  [...localEvents, ...dbEvents].forEach((ev) => {
    const evType = ev.event_type || ev.metadata?.action;
    const isEnquiry =
      evType === 'whatsapp_click' ||
      evType === 'call_click' ||
      evType === 'enquiry_click' ||
      ev.metadata?.channel === 'WhatsApp' ||
      ev.metadata?.channel === 'Call';

    if (isEnquiry) {
      const meta = ev.metadata || {};
      allEnquiries.push({
        id: ev.id,
        created_at: ev.created_at || new Date().toISOString(),
        type: evType === 'call_click' || meta.channel === 'Call' ? 'Call' : 'WhatsApp',
        source_page: ev.page_path || meta.source || '/',
        details: meta.button_text || meta.target_href || meta.phone_number || 'Direct Click',
        ip: ev.ip_hash || meta.ip || 'Unknown IP',
        city: meta.city || '-',
      });
    }
  });
  allEnquiries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return {
    visits: allVisits,
    bookings: allBookings,
    contacts: allContacts,
    enquiries: allEnquiries,
    pageClicks,
    pageSeoMap: masterPageSeo,
  };
}

export function filterAnalyticsByDate(data, startDate, endDate) {
  if (!startDate && !endDate) return data;

  const isWithin = (dateStr) => {
    if (!dateStr) return false;
    const d = dateStr.slice(0, 10);
    if (startDate && d < startDate) return false;
    if (endDate && d > endDate) return false;
    return true;
  };

  return {
    ...data,
    visits: (data.visits || []).filter((v) => isWithin(v.timestamp)),
    bookings: (data.bookings || []).filter((b) => isWithin(b.created_at)),
    contacts: (data.contacts || []).filter((c) => isWithin(c.created_at)),
    enquiries: (data.enquiries || []).filter((e) => isWithin(e.created_at)),
  };
}

export function aggregateAnalytics(data, period = 'day') {
  const { visits = [], bookings = [], contacts = [], enquiries = [] } = data;
  const groups = new Map();

  const getPeriodKey = (dateStr) => {
    const d = new Date(dateStr || Date.now());
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    if (period === 'month') {
      return `${year}-${month}`;
    }
    if (period === 'week') {
      // Calculate ISO week
      const target = new Date(d.valueOf());
      const dayNr = (d.getDay() + 6) % 7;
      target.setDate(target.getDate() - dayNr + 3);
      const firstThursday = target.valueOf();
      target.setMonth(0, 1);
      if (target.getDay() !== 4) {
        target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
      }
      const weekNumber = 1 + Math.ceil((firstThursday - target) / 604800000);
      return `${year}-W${String(weekNumber).padStart(2, '0')}`;
    }
    // Default day
    return `${year}-${month}-${day}`;
  };

  const formatPeriodLabel = (key) => {
    if (period === 'month') {
      const [y, m] = key.split('-');
      const date = new Date(Number(y), Number(m) - 1, 1);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (period === 'week') {
      return `Week ${key.split('-W')[1]}, ${key.split('-W')[0]}`;
    }
    const [y, m, d] = key.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getOrCreateGroup = (key) => {
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: formatPeriodLabel(key),
        ips: new Set(),
        visits: 0,
        clicks: 0,
        enquiries: 0,
        bookings: 0,
        contacts: 0,
      });
    }
    return groups.get(key);
  };

  // Populate Visits
  visits.forEach((v) => {
    const key = getPeriodKey(v.timestamp);
    const grp = getOrCreateGroup(key);
    grp.visits += 1;
    if (v.ip) grp.ips.add(v.ip);
    grp.clicks += Number(v.clicks) || 0;
  });

  // Populate Enquiries
  enquiries.forEach((e) => {
    const key = getPeriodKey(e.created_at);
    const grp = getOrCreateGroup(key);
    grp.enquiries += 1;
    if (e.ip) grp.ips.add(e.ip);
  });

  // Populate Bookings
  bookings.forEach((b) => {
    const key = getPeriodKey(b.created_at);
    const grp = getOrCreateGroup(key);
    grp.bookings += 1;
  });

  // Populate Contacts
  contacts.forEach((c) => {
    const key = getPeriodKey(c.created_at);
    const grp = getOrCreateGroup(key);
    grp.contacts += 1;
  });

  const sortedRows = Array.from(groups.values())
    .map((grp) => {
      const uniqueIps = grp.ips.size;
      const totalVisits = grp.visits;
      const totalClicks = grp.clicks;
      const avgClicksPerUser = uniqueIps > 0 ? (totalClicks / uniqueIps).toFixed(1) : '0.0';
      const conversions = grp.enquiries + grp.bookings + grp.contacts;
      const conversionRate = totalVisits > 0 ? ((conversions / totalVisits) * 100).toFixed(1) : '0.0';

      return {
        key: grp.key,
        label: grp.label,
        uniqueIps,
        totalVisits,
        totalClicks,
        avgClicksPerUser,
        enquiryClicks: grp.enquiries,
        bookRideForms: grp.bookings,
        contactForms: grp.contacts,
        conversionRate: `${conversionRate}%`,
      };
    })
    .sort((a, b) => b.key.localeCompare(a.key));

  return sortedRows;
}

export function getPageKeywordsPerformance(data) {
  const { visits = [], enquiries = [], pageClicks = {} } = data;
  const pageMap = new Map();

  const getOrCreate = (path) => {
    const cleanPath = path || '/';
    if (!pageMap.has(cleanPath)) {
      const seo = getPageSeoMetadata(cleanPath);
      pageMap.set(cleanPath, {
        page_path: cleanPath,
        title: seo.title,
        description: seo.description,
        keywords: seo.keywords,
        views: 0,
        clicks: pageClicks[cleanPath] || 0,
        ips: new Set(),
        enquiries: 0,
        last_visited: null,
      });
    }
    return pageMap.get(cleanPath);
  };

  // Populate from visits
  visits.forEach((v) => {
    const p = v.page_path || '/';
    const entry = getOrCreate(p);
    entry.views += 1;
    if (v.ip) entry.ips.add(v.ip);
    if (v.clicks && v.clicks > 0 && !pageClicks[p]) {
      entry.clicks += Number(v.clicks);
    }
    if (!entry.last_visited || new Date(v.timestamp) > new Date(entry.last_visited)) {
      entry.last_visited = v.timestamp;
    }
  });

  // Populate from enquiries
  enquiries.forEach((e) => {
    const p = e.source_page || '/';
    const entry = getOrCreate(p);
    entry.enquiries += 1;
    entry.clicks += 1;
  });

  // Ensure primary core pages are listed
  const corePaths = ['/', '/services', '/fleet', '/booking', '/contact', '/faqs', '/about', '/routes'];
  corePaths.forEach((p) => getOrCreate(p));

  const results = Array.from(pageMap.values()).map((p) => {
    const ctr = p.views > 0 ? (p.clicks / p.views).toFixed(1) : (p.clicks > 0 ? String(p.clicks) : '0.0');
    return {
      page_path: p.page_path,
      title: p.title,
      description: p.description,
      keywords: p.keywords,
      views: p.views,
      clicks: p.clicks,
      unique_ips: p.ips.size,
      enquiries: p.enquiries,
      ctr,
      last_visited: p.last_visited ? new Date(p.last_visited).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'No visits yet',
    };
  });

  // Sort descending by clicks, then views
  results.sort((a, b) => {
    if (b.clicks !== a.clicks) return b.clicks - a.clicks;
    return b.views - a.views;
  });

  return results;
}

export function exportReportToCsv(rows, periodName = 'daywise') {
  if (!rows || !rows.length) {
    alert('No data available to export.');
    return;
  }

  const headers = [
    'Period',
    'Unique Visitors (IPs)',
    'Total Page Visits',
    'Total Clicks',
    'Avg Clicks Per User',
    'Enquiry Clicks (WhatsApp/Call)',
    'Book Ride Forms Filled',
    'Contact Us Forms Filled',
    'Conversion Rate',
  ];

  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        `"${r.label}"`,
        r.uniqueIps,
        r.totalVisits,
        r.totalClicks,
        r.avgClicksPerUser,
        r.enquiryClicks,
        r.bookRideForms,
        r.contactForms,
        `"${r.conversionRate}"`,
      ].join(',')
    ),
  ];

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `trishul_analytics_${periodName}_report.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportPagesPerformanceCsv(rows) {
  if (!rows || !rows.length) {
    alert('No page data available to export.');
    return;
  }

  const headers = [
    'Rank',
    'Page Path',
    'Page Title',
    'Meta Description',
    'Focus Keywords',
    'Total Clicks',
    'Total Page Views',
    'Unique Visitors (IPs)',
    'Enquiry Clicks (WhatsApp/Call)',
    'Avg Clicks / View',
    'Last Visited',
  ];

  const csvRows = [
    headers.join(','),
    ...rows.map((r, idx) =>
      [
        idx + 1,
        `"${(r.page_path || '').replace(/"/g, '""')}"`,
        `"${(r.title || '').replace(/"/g, '""')}"`,
        `"${(r.description || '').replace(/"/g, '""')}"`,
        `"${(r.keywords || '').replace(/"/g, '""')}"`,
        r.clicks || 0,
        r.views || 0,
        r.unique_ips || 0,
        r.enquiries || 0,
        `"${r.ctr || '0.0'}"`,
        `"${r.last_visited || ''}"`,
      ].join(',')
    ),
  ];

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `trishul_page_keyword_clicks_report.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}